<template>
  <Form
    class="cs-availability-form"
    :class="{
      multiple: !isSinglePitchType,
    }"
    @submit="send"
  >
    <template v-if="isSinglePitchType">
      <div class="single-pt-notify-me">
        <div class="exclamation">
          <img
            loading="lazy"
            src="@/assets/images/notify-bell.svg"
            width="29"
            height="26"
          />
        </div>
        <translate>Alert me when there's availability...</translate>
      </div>
      <div class="single-pt">
        <base-checkbox :model-value="true" class="noselect">
          {{ pitchtypes[0].name }}
        </base-checkbox>
      </div>
    </template>

    <base-multi-checkbox
      v-if="!isSinglePitchType && !isSearchResultAlert"
      :options="pitchtypes"
      @change="updateSelectedPitchtypes"
    />

    <transition name="fade" tag="div">
      <div v-show="selectedPitchtypesIdsErrorMessage" class="input-error-msg">
        <translate v-if="selectedPitchtypesIdsErrorMessage === 'required'">
          Please choose the pitch types you're interested in
        </translate>
      </div>
    </transition>
    <base-input
      v-model="email"
      :value="email"
      :placeholder="$t('Add your email address…')"
      :validation-errors="emailErrorMessage"
      name="email"
      :required="true"
      type="email"
      class="email"
    >
      <template #label>
        <translate>Email address</translate>
      </template>
      <template #errorMessage="{ failedRules }">
        <translate v-if="failedRules.includes('required')" tag="div">
          Please enter your email address
        </translate>
        <translate v-if="failedRules.includes('email')" tag="div">
          Please enter a valid email address
        </translate>
        <template v-else-if="!failedRules.includes('required')">{{
          failedRules
        }}</template>
      </template>
    </base-input>
    <div class="button-wrapper">
      <base-submit-button class="secondary sml" :submitting="submitting">
        <translate>Request alert</translate>
        <template #submittingText>
          <translate>Saving...</translate>
        </template>
      </base-submit-button>
    </div>
  </Form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useMachine } from '@xstate/vue'
import { Form, useField } from 'vee-validate'
import * as z from 'zod'
import { SearchFilters } from '~/apps/search/schemas'
import { useAuthMixin } from '../../../mixins/AuthMixinNew'
import { getAllocationAlertStateMachine } from '../machine'
import { Party } from '../types'

const emailValidationRules = z
  .string({ required_error: 'required' })
  .min(1, { message: 'required' })
  .email({ message: 'email' })

const {
  value: email,
  errorMessage: emailErrorMessage,
  validate: validateEmail,
  handleChange,
  setErrors,
} = useField('email', toTypedSchema(emailValidationRules))

const props = defineProps<{
  searchFilters?: SearchFilters
  pitchtypes?: Array<{ id: string; name: string }>
  dateRange?: { start: string; end: string }
  party?: Party
  isSearchResultAlert?: boolean
}>()
const emit = defineEmits(['submitted'])

// when on low results search, selectedpitchtypesIds will be undefined
const selectedPitchtypesIdValidationRules = props.isSearchResultAlert
  ? z.undefined()
  : z
      .array(z.string(), { required_error: 'required' })
      .min(1, { message: 'required' })

const {
  value: selectedPitchtypesIds,
  errorMessage: selectedPitchtypesIdsErrorMessage,
  validate: validateSelectedPitchtypes,
} = useField(
  'selectedPitchtypesIds',
  toTypedSchema(selectedPitchtypesIdValidationRules),
)

function updateSelectedPitchtypes(event) {
  if (Array.isArray(event)) {
    selectedPitchtypesIds.value = event
  }
}

const currentLangCode = (useRoute().params.lang as string) || 'en-gb'
const { snapshot: allocationAlertState, send: allocationAlertSend } =
  useMachine(getAllocationAlertStateMachine(currentLangCode))

const { authState, authMachine, userEmail } = useAuthMixin(authMounted)

const isSinglePitchType = computed<boolean>(
  () => props.pitchtypes && props.pitchtypes.length === 1,
)

function listenToAuthMachineEvents() {
  if (authMachine) {
    authMachine.value.onTransition((state) => {
      if (state.changed) {
        authState.value = state
        populateEmailInput()
      }
    })
  }
}

function authMounted() {
  listenToAuthMachineEvents()
  if (isSinglePitchType.value) {
    selectedPitchtypesIds.value = [props.pitchtypes[0].id]
  }
  populateEmailInput()
}

function populateEmailInput() {
  if (userEmail.value) {
    handleChange(userEmail.value)
  }
}

watch(allocationAlertState, ({ value }) => {
  if (value === 'submitted') {
    emit('submitted')
  } else if (value === 'error') {
    setErrors(allocationAlertState.value.context.errorMessages)
  }
})

async function send() {
  const isSelectedPitchtypesValid = await validateSelectedPitchtypes()
  const isEmailValid = await validateEmail()
  if (isSelectedPitchtypesValid.valid && isEmailValid.valid) {
    props.isSearchResultAlert ? sendSearchResults() : sendCampsiteDetails()
  }
}

function sendCampsiteDetails() {
  allocationAlertSend({
    type: 'SUBMIT_CAMPSITE_DETAILS',
    payload: {
      email: email.value,
      arrive: props.dateRange.start,
      depart: props.dateRange.end,
      adults: props.party.adults.toString(),
      child_ages: props.party.childAges.join(','),
      dogs: props.party.dogs?.toString(),
      pitchtypesIds: selectedPitchtypesIds.value,
      langCode: currentLangCode,
    },
  })
}

function sendSearchResults() {
  allocationAlertSend({
    type: 'SUBMIT_SEARCH_RESULTS',
    email: email.value,
    searchFilters: props.searchFilters,
  })
}

const submitting = computed<boolean>(() => {
  if (allocationAlertState.value) {
    return allocationAlertState?.value.matches('submitting') || false
  }
  return false
})
</script>

<style scoped>
.cs-availability-form {
  padding: 1em;
  background: white;
  border-radius: 0.5em;
  margin-top: 0.5em;
}

.cs-availability-form.multiple {
  border-radius: 0 0 0.5em 0.5em;
  margin-top: 0;
}

.single-pt {
  display: none;
}

.single-pt-notify-me {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: 1.1;
  margin: 0 0 0.5em 0;
}

.single-pt-notify-me span {
  font-weight: bold;
  font-size: 1em;
  color: var(--colour-dark-blue);
}

.single-pt-notify-me :deep(label) {
  font-weight: normal;
  font-size: var(--small);
}

.exclamation {
  flex-shrink: 0;
  height: auto;
  margin: 0 0.5em 0 -0.2em;
  width: 1.5em;
}

.noselect {
  pointer-events: none;
}

.button-wrapper {
  text-align: right;
  padding: 1em 0 0;
}
</style>
